import React, { useMemo } from 'react'
import { Container } from 'react-bootstrap'
import './AccountStyle.css'
import useAccounts from './useAccounts'
import { AccountsHeaderProvider } from './AccountsHeader.context'
import { ProfileProvider, useProfile } from './AccountsProfile.context'
import StaffGate from './StaffGate'
import {
  PersonalInfo,
  ProfessionalInfo,
  PasswordSecurity,
  WorkInfo,
} from '@components/pages/Accounts'
import { EmptyPlaceholder, InputField } from '@components/common'

function AccountsShell(): React.ReactElement {
  const hooks = useAccounts()
  const { loadState, pid } = useProfile()

  const mainContent = useMemo((): React.ReactNode => {
    switch (hooks.activeItem?.id) {
      case 'account-personal-info':
        return <PersonalInfo />
      case 'account-professional-info':
        return <ProfessionalInfo />
      case 'account-work-schedule':
        return <WorkInfo />
      case 'account-password-security':
        return <PasswordSecurity />
      default:
        return null
    }
  }, [hooks.activeItem?.id])

  const highlightText = (
    text: string,
    shouldHighlight: boolean,
  ): React.ReactNode => {
    const normalizedQuery = hooks.searchQuery.trim().toLowerCase()
    if (!normalizedQuery || !shouldHighlight) {
      return text
    }

    const lowerText = text.toLowerCase()
    const index = lowerText.indexOf(normalizedQuery)

    if (index === -1) {
      return <span className="acp-search-highlight">{text}</span>
    }

    const before = text.slice(0, index)
    const match = text.slice(index, index + normalizedQuery.length)
    const after = text.slice(index + normalizedQuery.length)

    return (
      <>
        {before}
        <span className="acp-search-highlight">{match}</span>
        {after}
      </>
    )
  }

  if (loadState === 'idle' || loadState === 'loading') {
    return (
      <Container
        fluid
        className="accounts-container-page"
        id="accounts-settings-page"
      />
    )
  }

  if (!pid || loadState === 'error') {
    return (
      <Container
        fluid
        className="accounts-container-page py-5 text-center"
        id="accounts-settings-page"
      >
        <p>Unable to load your account profile.</p>
      </Container>
    )
  }

  return (
    <AccountsHeaderProvider value={hooks.headerContextValue}>
      <Container
        fluid
        className="accounts-container-page"
        id="accounts-settings-page"
      >
        <div className="acp-nav-bar">
          {hooks.showSidebar && (
            <InputField
              placeholder="Search"
              leftIcon={<span className="mgc_search_line" />}
              rightIcon={
                hooks.searchQuery.length > 0 ? (
                  <span className="mgc_close_line" />
                ) : undefined
              }
              value={hooks.searchQuery}
              onChange={hooks.handleSearch}
              onRightIconClick={hooks.handleClearSearch}
            />
          )}
          <div className="acp-mc-header">
            <div className="accounts-title">{hooks.headerTitle}</div>
            <div className="component-buttons">{hooks.headerButtons}</div>
          </div>
        </div>

        <div className="acp-main-content-wrapper">
          {hooks.showSidebar && (
            <div className="acp-side-panel-wrapper">
              <div className="acp-side-panel">
                <div className="acp-side-panel-item" key="accounts-title">
                  <span className="accounts-title">Accounts</span>
                  <button
                    className={`unstyled-button acp-side-panel-icon mgc_list_${hooks.expandedItems.length ? 'collapse' : 'expansion'}_line`}
                    role="button"
                    onClick={hooks.toggleAllItems}
                    title={
                      hooks.expandedItems.length ? 'Collapse all' : 'Expand all'
                    }
                    aria-label={
                      hooks.expandedItems.length ? 'Collapse all' : 'Expand all'
                    }
                    data-bs-toggle="tooltip"
                  />
                </div>
                <div className="acp-side-panel-scrollable">
                  {hooks.filteredNavItems.length > 0 ? (
                    hooks.filteredNavItems.map((item) => {
                      const normalizedQuery = hooks.searchQuery
                        .trim()
                        .toLowerCase()
                      const itemMatches =
                        !!normalizedQuery &&
                        (item.id.toLowerCase().includes(normalizedQuery) ||
                          item.name.toLowerCase().includes(normalizedQuery))

                      return (
                        <div
                          className="acp-side-panel-item-expand"
                          key={item.id}
                        >
                          <button
                            className={`unstyled-button acp-side-panel-item${
                              item.items.length === 0 &&
                              hooks.activeItem?.id === item.id
                                ? ' active'
                                : ''
                            }`}
                            role="button"
                            onClick={(e) => hooks.toggleItem(e, item.id)}
                          >
                            <span>
                              <span className={`acp-spi-icon ${item.icon}`} />
                              {highlightText(item.name, itemMatches)}
                            </span>
                            {item.items.length > 0 && (
                              <span
                                className={`acp-side-panel-icon mgc_${
                                  hooks.expandedItems.includes(item.id)
                                    ? 'down'
                                    : 'right'
                                }_small_fill`}
                                title={
                                  hooks.expandedItems.length
                                    ? 'Collapse'
                                    : 'Expand'
                                }
                                data-bs-toggle="tooltip"
                              />
                            )}
                          </button>
                          {hooks.expandedItems.includes(item.id) &&
                            item.items.map((subItem) => {
                              const subItemMatches =
                                !!normalizedQuery &&
                                subItem.name
                                  .toLowerCase()
                                  .includes(normalizedQuery)

                              return (
                                <button
                                  className={`acp-side-panel-sub-item ${
                                    hooks.activeItem?.id === subItem.id
                                      ? 'active'
                                      : ''
                                  }`}
                                  key={`${item.id}-${subItem.id}`}
                                  role="button"
                                  onClick={(e) =>
                                    hooks.onSubItemClick(e, subItem)
                                  }
                                >
                                  <span>
                                    {highlightText(
                                      subItem.name,
                                      subItemMatches,
                                    )}
                                  </span>
                                </button>
                              )
                            })}
                        </div>
                      )
                    })
                  ) : (
                    <EmptyPlaceholder
                      title="No accounts found"
                      content="Try a different search term."
                      icon="mgc_search_line"
                      className="acp-empty-placeholder"
                    />
                  )}
                </div>
                <div className="acp-side-panel-footer">
                  <span className="acp-side-panel-footer-pill">
                    HIPAA Compliant
                  </span>
                  <span>ENAMELAI ver. 1.0.0</span>
                </div>
              </div>
            </div>
          )}
          <div className="acp-main-content">{mainContent}</div>
        </div>
      </Container>
    </AccountsHeaderProvider>
  )
}

function Accounts(): React.ReactElement {
  return (
    <StaffGate>
      <ProfileProvider>
        <AccountsShell />
      </ProfileProvider>
    </StaffGate>
  )
}

export default Accounts
